import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { ExportImportButtons } from "@/components/ExportImportButtons";

/**
 * Unified Spares Parts Entry — replaces the separate Inventory + Stock Entry screens.
 * Spares Assistant uses this single page to:
 *   • Search/filter spares (code, name, brand, product category, model compatibility)
 *   • Edit compatibility mapping per part (no stock duplication across models)
 *   • Create MRN (the only path to increase stock)
 * Existing stock is preserved — this page reads the same /api/parts collection.
 */
export default function SparesPartsEntry() {
  const [parts, setParts] = useState([]);
  const [filters, setFilters] = useState({ search: "", brand: "", product_category: "", model_number: "", condition: "", rack_number: "", box_number: "" });
  const [editing, setEditing] = useState(null);
  const [mrnOpen, setMrnOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/spares/parts", { params, _: Date.now() });
      setParts(data || []);
    } catch (e) {
      toast.error("Could not load parts", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5" data-testid="parts-entry-page">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Spares Parts Entry</h1>
          <p className="text-sm text-slate-500 mt-1">
            One unified module for spare parts, compatibility mapping, and MRN-based stock entry.
          </p>
        </div>
        <div className="flex gap-2">
          <ExportImportButtons kind="parts_inventory" label="Part Inventory" onDone={load} />
          <Button onClick={() => setMrnOpen(true)} className="bg-slate-900 text-white" data-testid="open-mrn-dialog">
            <Plus size={14} className="mr-1" /> Create MRN
          </Button>
          <Link to="/spares/mrn"><Button variant="outline" data-testid="view-mrns">View MRNs</Button></Link>
        </div>
      </div>

      <Card><CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Fld label="Search"><Input value={filters.search} onChange={(e) => update("search", e.target.value)} placeholder="code or name" data-testid="filter-search" /></Fld>
          <Fld label="Brand"><Input value={filters.brand} onChange={(e) => update("brand", e.target.value)} placeholder="exact brand" data-testid="filter-brand" /></Fld>
          <Fld label="Category"><Input value={filters.product_category} onChange={(e) => update("product_category", e.target.value)} placeholder="e.g. AC" data-testid="filter-category" /></Fld>
          <Fld label="Model Number"><Input value={filters.model_number} onChange={(e) => update("model_number", e.target.value)} placeholder="substring" data-testid="filter-model" /></Fld>
          <Fld label="Condition">
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                    value={filters.condition} onChange={(e) => update("condition", e.target.value)}
                    data-testid="filter-condition">
              <option value="">All</option>
              <option value="Fresh">Fresh</option>
              <option value="Damaged">Damaged</option>
              <option value="Refurbished">Refurbished</option>
              <option value="Scrap">Scrap</option>
            </select>
          </Fld>
          <Fld label="Rack Number"><Input value={filters.rack_number} onChange={(e) => update("rack_number", e.target.value)} placeholder="substring" data-testid="filter-rack" /></Fld>
          <Fld label="Box Number"><Input value={filters.box_number} onChange={(e) => update("box_number", e.target.value)} placeholder="substring" data-testid="filter-box" /></Fld>
          <div className="flex items-end"><Button onClick={load} className="bg-slate-900 text-white w-full" data-testid="apply-filters">Apply</Button></div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <Th>Part Code</Th><Th>Part Name</Th><Th>Brand</Th><Th>Compatibility</Th>
                <Th>Rack / Box</Th>
                <Th align="right">Fresh<br/><span className="text-[9px] font-normal text-slate-500">KTM/BRT</span></Th>
                <Th align="right">Damaged<br/><span className="text-[9px] font-normal text-slate-500">KTM/BRT</span></Th>
                <Th align="right">Refurb<br/><span className="text-[9px] font-normal text-slate-500">KTM/BRT</span></Th>
                <Th align="right">Scrap<br/><span className="text-[9px] font-normal text-slate-500">KTM/BRT</span></Th>
                <Th align="right">Total</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="text-center py-12 text-slate-400">Loading…</td></tr>}
              {!loading && parts.length === 0 && <tr><td colSpan={11} className="text-center py-12 text-slate-400">No parts.</td></tr>}
              {parts.map((p) => {
                const fresh = (p.qty_ktm || 0) + (p.qty_brt || 0);
                const dmg = (p.qty_ktm_damaged || 0) + (p.qty_brt_damaged || 0);
                const rfb = (p.qty_ktm_refurbished || 0) + (p.qty_brt_refurbished || 0);
                const scp = (p.qty_ktm_scrap || 0) + (p.qty_brt_scrap || 0);
                const tot = fresh + dmg + rfb + scp;
                return (
                  <tr key={p.id} data-testid={`pe-row-${p.id}`}>
                    <td className="font-mono">{p.part_code || "—"}</td>
                    <td>{p.part_name}</td>
                    <td>{p.brand || "—"}</td>
                    <td className="text-xs text-slate-700">
                      {(p.compatible_brands || []).slice(0, 3).join(", ")}
                      {(p.compatible_model_numbers || []).length > 0 && (
                        <div className="font-mono text-slate-500">
                          Models: {(p.compatible_model_numbers || []).slice(0, 4).join(", ")}
                          {(p.compatible_model_numbers || []).length > 4 ? ` (+${p.compatible_model_numbers.length - 4})` : ""}
                        </div>
                      )}
                    </td>
                    <td className="text-xs font-mono" data-testid={`pe-rackbox-${p.id}`}>{p.rack_number || "—"} / {p.box_number || "—"}</td>
                    <td className="text-right font-mono text-emerald-700" data-testid={`pe-fresh-${p.id}`}>{p.qty_ktm || 0} / {p.qty_brt || 0}</td>
                    <td className="text-right font-mono text-rose-700" data-testid={`pe-damaged-${p.id}`}>{p.qty_ktm_damaged || 0} / {p.qty_brt_damaged || 0}</td>
                    <td className="text-right font-mono text-amber-700" data-testid={`pe-refurb-${p.id}`}>{p.qty_ktm_refurbished || 0} / {p.qty_brt_refurbished || 0}</td>
                    <td className="text-right font-mono text-slate-500" data-testid={`pe-scrap-${p.id}`}>{p.qty_ktm_scrap || 0} / {p.qty_brt_scrap || 0}</td>
                    <td className="text-right font-mono font-semibold">{tot}</td>
                    <td className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`edit-compat-${p.id}`}>
                        Compatibility
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <CompatibilityDialog open={!!editing} part={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <MRNDialog open={mrnOpen} onClose={() => setMrnOpen(false)} onSaved={() => { setMrnOpen(false); load(); }} parts={parts} />
    </div>
  );
}

function CompatibilityDialog({ open, part, onClose, onSaved }) {
  const [form, setForm] = useState({ compatible_brands: "", compatible_product_categories: "", compatible_product_names: "", compatible_model_numbers: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (part) {
      setForm({
        compatible_brands: (part.compatible_brands || []).join(", "),
        compatible_product_categories: (part.compatible_product_categories || []).join(", "),
        compatible_product_names: (part.compatible_product_names || []).join(", "),
        compatible_model_numbers: (part.compatible_model_numbers || []).join(", "),
      });
    }
  }, [part]);
  if (!part) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const toArr = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
      await api.put(`/spares/parts/${part.id}/compatibility`, {
        compatible_brands: toArr(form.compatible_brands),
        compatible_product_categories: toArr(form.compatible_product_categories),
        compatible_product_names: toArr(form.compatible_product_names),
        compatible_model_numbers: toArr(form.compatible_model_numbers),
      });
      toast.success("Compatibility saved (stock unchanged)");
      onSaved();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Compatibility · {part.part_name}</DialogTitle>
          <DialogDescription>Comma-separated values. Stock quantity is NEVER duplicated when you add models.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <Fld label="Compatible Brands"><Input value={form.compatible_brands} onChange={(e) => setForm({ ...form, compatible_brands: e.target.value })} data-testid="cb-input" placeholder="HISENSE, VOLTAS" /></Fld>
          <Fld label="Compatible Product Categories"><Input value={form.compatible_product_categories} onChange={(e) => setForm({ ...form, compatible_product_categories: e.target.value })} data-testid="cpc-input" placeholder="AC, TV" /></Fld>
          <Fld label="Compatible Product Names / Types"><Input value={form.compatible_product_names} onChange={(e) => setForm({ ...form, compatible_product_names: e.target.value })} data-testid="cpn-input" placeholder="1.5T AC, Smart TV" /></Fld>
          <Fld label="Compatible Model Numbers"><Input value={form.compatible_model_numbers} onChange={(e) => setForm({ ...form, compatible_model_numbers: e.target.value })} data-testid="cmn-input" placeholder="AC-1234, TV-5050" /></Fld>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-900 text-white" data-testid="save-compat">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MRNDialog({ open, onClose, onSaved }) {
  const blank = {
    mrn_date: new Date().toISOString().slice(0, 10),
    source_type: "purchase", part_code: "", part_name: "", brand: "",
    quantity: 1, location: "KTM", condition: "new",
    complaint_number: "", source_unit_brand: "", source_unit_product: "",
    source_unit_model: "", source_unit_serial: "", remarks: "",
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank); /* eslint-disable-next-line */ }, [open]);
  const submit = async () => {
    if (!form.part_name.trim()) { toast.error("Part name required"); return; }
    if (form.quantity <= 0) { toast.error("Quantity must be > 0"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/spares/mrn", { ...form, quantity: Number(form.quantity) });
      toast.success(`MRN ${data.mrn_number} created · stock +${data.quantity}`);
      onSaved();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create MRN (Material Receipt Note)</DialogTitle>
          <DialogDescription>The only path to increase stock. Every MRN posts to the stock ledger and is printable.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Fld label="MRN Date"><Input type="date" value={form.mrn_date} onChange={(e) => setForm({ ...form, mrn_date: e.target.value })} data-testid="mrn-date" /></Fld>
          <Fld label="Source Type">
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                    value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })}
                    data-testid="mrn-source">
              <option value="purchase">Fresh Purchase</option>
              <option value="fresh_unit">Taken from Fresh Unit</option>
              <option value="damaged_set">Taken from Damaged Set</option>
              <option value="refurbished_set">Taken from Refurbished Set</option>
              <option value="technician_return">Technician Return</option>
              <option value="adjustment">Adjustment / Other</option>
            </select>
          </Fld>
          <Fld label="Part Code"><Input value={form.part_code} onChange={(e) => setForm({ ...form, part_code: e.target.value })} data-testid="mrn-part-code" /></Fld>
          <Fld label="Part Name *"><Input value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} data-testid="mrn-part-name" /></Fld>
          <Fld label="Brand"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="mrn-brand" /></Fld>
          <Fld label="Quantity *"><Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })} data-testid="mrn-qty" /></Fld>
          <Fld label="Location">
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                    value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="mrn-loc">
              <option value="KTM">KTM</option><option value="BRT">BRT</option>
            </select>
          </Fld>
          <Fld label="Condition">
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm bg-white"
                    value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} data-testid="mrn-condition">
              <option value="new">New</option><option value="used">Used</option>
              <option value="refurbished">Refurbished</option><option value="damaged">Damaged</option>
              <option value="for_testing">For Testing</option>
            </select>
          </Fld>
          <Fld label="Linked Complaint #"><Input value={form.complaint_number} onChange={(e) => setForm({ ...form, complaint_number: e.target.value })} placeholder="VI-..." data-testid="mrn-complaint" /></Fld>
          <Fld label="Source Unit Brand"><Input value={form.source_unit_brand} onChange={(e) => setForm({ ...form, source_unit_brand: e.target.value })} /></Fld>
          <Fld label="Source Unit Product"><Input value={form.source_unit_product} onChange={(e) => setForm({ ...form, source_unit_product: e.target.value })} /></Fld>
          <Fld label="Source Unit Model"><Input value={form.source_unit_model} onChange={(e) => setForm({ ...form, source_unit_model: e.target.value })} /></Fld>
          <Fld label="Source Unit Serial"><Input value={form.source_unit_serial} onChange={(e) => setForm({ ...form, source_unit_serial: e.target.value })} /></Fld>
          <div className="col-span-2"><Fld label="Remarks"><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="mrn-remarks" /></Fld></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-slate-900 text-white" data-testid="mrn-submit">{busy ? "Creating…" : "Create MRN"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold px-2 py-2`}>{children}</th>;
}
